/**
 * A series of buffered method invocations.
 */
type CallBuffer = {
  /**
   * If true, the buffer has been processed, and will no longer be applied; if false, argument
   * arrays will continue to be appended, and will be processed when the prerequisite method has
   * finished executing.
   */
  flushed: boolean;
  /** A list of one argument array for each invocation of the decorated method. */
  calls: any[][];
};

/**
 * Returns the {@link CallBuffer} for the given method, creating it if necessary.
 */
const getCallBuffer = (o: any, propertyKey: string): CallBuffer => {
  const callBufferKey = `__callBuffer__${propertyKey}__`;
  // Create empty call buffer if necessary.
  if (o[callBufferKey] === undefined) {
    Object.defineProperty(o, callBufferKey, {
      value: {
        flushed: false,
        calls: []
      }
    });
  }
  // We know call buffer exists and is of correct type since we created it if it was undefined.
  return o[callBufferKey];
};

/**
 * Returns a decorator whose target will buffer invocations until the specified prerequisite method
 * has executed at least once.
 *
 * @remarks
 * This is useful for creating methods that require initialization logic to function, but are to be
 * invokable regardless of their containing object's initialization status.
 *
 * @param prerequisiteMethodName The name of the method for which to wait.
 *
 * @example
 * ```ts
 * class Logger {
 *   async initialize(): Promise<void> {
 *     // ...
 *   }
 *
 *   @waitFor('initialize') // Logger.log will wait until Logger.initialize is called.
 *   log(message: string) {
 *     console.log(message);
 *   }
 * }
 *
 * const logger = new Logger();
 * logger.log('a'); // Prints nothing -- the call is buffered.
 * logger.log('b'); // Prints nothing -- the call is buffered.
 * await logger.initialize(); // Prints 'a' then 'b' -- the buffered calls are invoked.
 * logger.log('c'); // Prints 'c' -- logger has been initialized; calls to Logger.log go through.
 * ```
 */
export default function waitFor<M extends string>(prerequisiteMethodName: M) {
  return function(
    target: {
      [N in M]: (...args: any[]) => Promise<void>;
    },
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...args: any[]) => void>
  ) {
    // Original method, to be decorated
    const originalMethod = descriptor.value;

    // The prerequisite method, i.e., the method we want to wait for
    const prerequisiteMethod = target[prerequisiteMethodName];

    // Replace the definition of the prerequisite method with one that invokes the original
    // prerequisite method and then applies buffered invocations of the decorated method
    Object.defineProperty(target, prerequisiteMethodName, {
      async value(...args: any[]) {
        // Invoke original prerequisite method as normal
        await prerequisiteMethod.apply(this, args);

        // Obtain buffered invocations of the decorated method
        const callBuffer = getCallBuffer(this, propertyKey);
        if (callBuffer.flushed) {
          // If the call buffer has already been flushed, there's nothing more to do
          return;
        }
        // Apply buffered call invocations in order
        callBuffer.calls.forEach(
          (bufferedArgs: any[]) => originalMethod && originalMethod.apply(this, bufferedArgs)
        );
        // Mark the buffer as flushed so we don't invoke it again
        callBuffer.flushed = true;
        // Clear the array of buffered argument arrays to avoid memory leaks
        callBuffer.calls = [];
      }
    });

    // Replace the definition of the decorated method with one that buffers its invocation if the
    // prerequisite method has not yet been called
    descriptor.value = function(...args: any[]) {
      // Obtain buffered invocations of the decorated method
      const callBuffer = getCallBuffer(this, propertyKey);

      // If call buffer is flushed (i.e., the prerequisite method has executed), we just invoke the
      // original decorated method
      if (callBuffer.flushed) {
        originalMethod && originalMethod.apply(this, args);
        return;
      }

      // If we're still waiting for the prerequisite method to be called, we buffer the invocation
      callBuffer.calls.push(args);
    };
  };
}
